import React, { useState, useEffect, useContext, useMemo, createContext } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import ErrorBoundary from "./ErrorBoundry";
import AuthLayout from "./components/Layout/AuthLayout";
import Dashboard from "./pages/Dashboard";
import ProfilePage from "./pages/ProfilePage";
import FeedPage from "./pages/FeedPage";
import LeaderBoardPage from "./pages/LeaderBoardPage";
import SearchPage from "./pages/SearchPage";
import CreatePostPage from "./pages/CreatePostPage";
import MarketSentimentPage from "./pages/MarketSentimentPage";
import ChatPage from "./pages/ChatPage";
import ChatHistoryPage from "./pages/ChatHistoryPage";
import BuySellPage from "./pages/BuySell";
import ChartsPage from "./pages/ChartsPage";
import LiveChart from "./pages/LiveChart";
import Settings from "./pages/Settings";
import Blank from "./pages/Blank";
import HomePage from "./pages/HomePage";
import MobileOnboarding from "./pages/auth/Onboarding/MobileOnboarding";
import Login from "./pages/auth2/Login";
import Verification from "./pages/VerificationPage";
import Register from "./pages/auth2/Register";
import Subscriptions from "./pages/Subscription";
import Notifications from "./pages/Notifications";
import { useNavigate } from "react-router-dom";
import Password from "./pages/ResetPassword";
import ResetPasswordToken from "./pages/ResetPasswordToken";
import ChangePassword from "./pages/ChangePassword";
import EditProfile from "./pages/EditProfile";
import GetHelp from "./pages/GetHelp";
import AboutQushi from "./pages/AboutQushi";
import "react-toastify/dist/ReactToastify.css";
import SavedPost from "./pages/SavedPost";
import { SocketContext } from "./utils/socketio";
import StripePayment from "./pages/stripe-payment";
import Char from "./pages/char";
import $ from "jquery";
import PaidContent from "./pages/PaidContent";
import { useLocation } from "react-router-dom";
import CliffNotes from "./pages/CliffNotes";
import LegalPopupModal from "./components/Modals/legalDisclaimer";
import { toast } from "react-toastify";
import { legalDisclaimerApi, SocialLogin, getGuideStatus } from "./api/user";
import { legalAcceptedFlag } from "./utils/legalAcceptance";
import Contest from "./pages/Contest";
import MyGames from "./pages/Game/MyGames";
import FindGames from "./pages/Game/FindGames";
import PublicLeagues from "./pages/Game/PublicLeagues";
import MyLeagues from "./pages/Game/MyLeagues";
import LeaguePlayLanding from "./pages/Game/LeaguePlayLanding";
// Frozen pre-revamp League Play landing, mounted at /oldcontest for comparison.
import OldFindGames from "./pages/Game/legacy/OldFindGames";
import CreatePublicLeagueWizard from "./pages/Game/landing/CreatePublicLeagueWizard";
import CreatePrivateLeagueWizard from "./pages/Game/landing/CreatePrivateLeagueWizard";
import PrivateLeagueCreatedSuccess from "./pages/Game/landing/PrivateLeagueCreatedSuccess";
import PublicLeagueCreatedSuccess from "./pages/Game/landing/PublicLeagueCreatedSuccess";
import JoinLinkRedirect from "./pages/Game/landing/JoinLinkRedirect";
import {
  LegacyEditLeagueRedirect,
  LegacyBuySellRedirect,
  LegacyLeaguePortfolioRedirect,
} from "./pages/LegacyRedirects";
import PrivateLeaguePreview from "./pages/Game/landing/PrivateLeaguePreview";
import PrivateLeagueJoinedSuccess from "./pages/Game/landing/PrivateLeagueJoinedSuccess";
import GameContextProvider from "./store/game/GameStore";
import DetailGame from "./pages/Game/DetailGame";
import LeagueEntry from "./pages/Game/league/LeagueEntry";
import LeagueTradePage from "./pages/Game/league/LeagueTradePage";
import LeaguePortfolio from "./pages/Game/league/LeaguePortfolio";
import LeagueDetails from "./pages/Game/league/LeagueDetails";
import LeagueSettings from "./pages/Game/league/LeagueSettings";
import GameDetail from "./pages/Game/GameDetail";
import GameCharts from "./pages/Game/GameCharts";
import GameUserPortfolio from "./pages/Game/GameUserPortfolio";
import GameLeaderboard from "./pages/Game/GameLeaderboard";
import LeagueLeaderboardStandalone from "./pages/Game/LeagueLeaderboardStandalone";
import GameProfile from "./pages/Game/GameProfile";
import LeagueWatchlist from "./pages/Game/league/LeagueWatchlist";
import "./css/Game/game.css";
import GamePosts from "./pages/Game/GamePosts";
import GameMessage from "./pages/Game/GameMessage";
import GameCreatePost from "./pages/Game/GameCreatePost";
import FollowersPage from "./pages/FollowersPage";
import LeagueFollowerList from "./pages/Game/LeagueFollowerList";
import PerformancePage from "./pages/PerformancePage";
import WebAppleLogin from "./pages/auth2/Login/WebAppleLogin";
import Testing from "./pages/auth2/Login/Testing";
// The Cash Games "View All": three phase tabs, the metabar and the cash-game
// rows. Replaces the pre-revamp CashLeagues card grid, which is still on disk
// but no longer routed — nothing links to it.
import CashGames from "./pages/Game/CashGames";
import CompetitionRules from "./pages/Game/CompetitionRules";
import TradingFloor from "./pages/TradingFloor";
import TradingDetail from "./pages/TradingDetail";
import TradingSingle from "./pages/TradingSingle";
import { trackPageView } from "./api/user/index";
import MobileWatchlistPage from "./pages/MobileWatchlistPage";
import AppLinksMetaTags from "./AppLinksMetaTags";
import BeforeSearch from "./pages/BeforeSearch";
import ChildWatchlistPage from "./pages/ChildWatchlistPage";
import GuideSteps from "./pages/GuideSteps";
import Guide from "./pages/Guide";
import CookiePolicy from "./pages/CookiePolicy";
import CandleAnatomyPage from "./pages/Edge/CandleAnatomyPage";
import MomentumPage from "./pages/Edge/MomentumPage";
import StructurePage from "./pages/Edge/StructurePage";
import { setSidebarCollapseOverride } from "./utils/sidebarCollapse";

// Routes that a signed-out visitor is allowed to reach. Hoisted out of App()
// with the two gate components below — it is a constant, and rebuilding the
// array on every render served no purpose.
const unauthenticatedRoutes = [
  "/auth/login",
  "/auth/verification",
  "/auth/register",
  "/auth/create-account",
  "/auth/forgot-password",
  "/reset-password",
  "/callback",
  "/^\/trading-detail\/[^/]+\/[^/]+$/"
];

// ---------------------------------------------------------------------------
// Route gates
//
// `ProtectedRoute`, `IsUserVerified` and `Tracker` used to be declared INSIDE
// App(). That is the bug behind "the league page snaps back while I scroll and I
// can never reach the Join button".
//
// A component declared in a render body is a NEW function object on every
// render, and React identifies a component by its function REFERENCE. So on each
// App re-render React did not see "the same gate with new props" — it saw a
// different component type, and the only thing it can do with a changed type is
// throw the old tree away and mount a fresh one. Every screen under these gates
// was unmounted and remounted, losing all of its state.
//
// On a league page that meant LeagueEntry's `game`/`resolved` state reset to its
// initial values, so the page fell back to its full-page loading skeleton
// mid-scroll. Measured on an iPhone by the Flutter developer:
// document.body.scrollHeight collapsed from 2087px to 777px while scrolling, and
// a hit-test at screen centre returned the skeleton element. With the document
// suddenly shorter than the scroll offset, the browser clamps scrollTop — which
// is exactly the "rubber band" the client reported. LeagueHome's mount effect
// then ran scrollToTop() again, because a remount is a mount.
//
// It was never about the share sheet: the developer's trace recorded zero
// lifecycle events (the app never backgrounded) and it reproduces in plain
// mobile Safari. The share was a coincidence of timing in the client's video.
//
// Module scope makes the reference stable, so React updates these in place
// instead of remounting. The gate LOGIC below is copied verbatim — the values it
// reads now arrive by context instead of by closure, and nothing else changes.
const RouteGateContext = createContext({
  status: null,
  isAuthenticated: false,
  isVerified: false,
});

const ProtectedRoute = ({ element, path }) => {
  const { status, isAuthenticated } = useContext(RouteGateContext);

  return status || isAuthenticated || unauthenticatedRoutes.includes(path) ? (
    element
  ) : (
    <Navigate to="/" replace />
  );
};

const IsUserVerified = ({ path }) => {
  const { isAuthenticated, isVerified } = useContext(RouteGateContext);

  return isAuthenticated && !isVerified ? (
    <Navigate to="/auth/verification" replace />
  ) : (
    <>
      {window.location.pathname.includes("trading-floor") &&
        localStorage.setItem("redirectUrl", window.location.pathname)}
      <Navigate to="/" replace />
    </>
  );
};

function Tracker() {
  const location = useLocation();

  useEffect(() => {
    trackPageView(); // This will now send full URL like /buysell or /chart
  }, [location]);

  return null;
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(
    !!JSON.parse(localStorage.getItem("accessToken"))
  );
  const [isVerified, setIsVerified] = useState(false);
  const [isVerifiedOpen, setIsVerifiedOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  // Setter only: the native social-login bridge still flips this on its entry
  // and exit paths, but nothing renders from it now that the app-wide loading
  // overlay is gone.
  const [, setLoading] = useState(false);
  const [showPopup, setShowPopUp] = useState(false);
  const [checked, setChecked] = useState(false);
  const location = useNavigate();
  const socket = useContext(SocketContext);
  const status = JSON.parse(localStorage.getItem("isVerified"));
  const location1 = useLocation();

  // What the two hoisted gates above used to read from this closure. Same
  // values, same render, delivered by context so the gates can keep a stable
  // identity. Memoised so an unrelated App re-render does not churn consumers.
  const routeGate = useMemo(
    () => ({ status, isAuthenticated, isVerified }),
    [status, isAuthenticated, isVerified]
  );

  const handleCheckboxChange1 = () => {
    setChecked(!checked);
  };

  const handleLegalPopup = async () => {
    const userInfo = JSON.parse(localStorage.getItem("userInfo"));
    try {
      const res = await legalDisclaimerApi(checked);
      if (res?.data?.status == 200) {
        setShowPopUp(false);
        if (userInfo?.user_name) {
          location("/profile");
        } else {
          location("/edit-profile");
        }
        localStorage.setItem("checkDisclaimer", false);
      }
    } catch (err) {}
  };

  const handleClose = () => {
    location("/profile");
    localStorage.setItem("checkDisclaimer", false);
    return setShowPopUp(false);
  };

  window.sendGoogleData = function (data) {
    const jsonData = JSON.stringify(data);
    const data2 = JSON.parse(jsonData);
    // Native Google and web Apple both finish here. Carry the legal + age
    // acceptance through - the backend only looks at it when the call creates a
    // new account, so existing users signing in are unaffected.
    data2.legal_accepted = legalAcceptedFlag();
    // Native Google sign-in posts its result here. If the user dismissed the
    // account chooser (tapped the X), the bridge fires with no social_id — bail
    // before hitting the backend, otherwise it returns "social id field is
    // required" and the spinner buffers forever.
    if (!data2 || !data2.social_id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    SocialLogin(data2)
      .then((response) => {
        if (response?.data?.status == 200) {
          if (response?.data?.data?.disclaimer == false) {
            setShowPopUp(true);
            localStorage.setItem("checkDisclaimer", true);
          } else {
            location("/profile");
          }
          console.log("RESPONSE: ", response);
          const token = response.data.data.access_token;
          localStorage.setItem("google_login", "1");
          localStorage.setItem(
            "isVerified",
            response.data.data.is_otp_verified
          );
          localStorage.setItem("img", response.data.data.image);
          localStorage.setItem("userInfo", JSON.stringify(response.data.data));
          localStorage.setItem("accessToken", JSON.stringify(token));
          localStorage.setItem("user_id", response.data.data.id);
          setSidebarCollapseOverride(null);
          setLoading(false);
        } else {
          // Backend rejected the login — clear the spinner so it can't buffer.
          setLoading(false);
          toast.error(
            {
              "This email ID is linked to another account. Please contact the support team for assistance.":
                "An account with this email already exists. Log in the way you signed up, or contact support.",
              "This phone number is linked to another account. Please contact the support team for assistance.":
                "An account with this phone number already exists. Log in the way you signed up, or contact support.",
              "Please confirm your agreement and age eligibility to continue.":
                "Confirm the Terms of Service, Privacy Policy, and age requirement.",
            }[response?.data?.message] ||
              "Could not log in. Check your information and try again."
          );
        }
      })
      .catch((error) => {
        setLoading(false);
      });
  };

  useEffect(() => {
    // Check if the route is not /buysell or /buysell/:id
    if (
      location1.pathname !== "/buysell" &&
      !location1.pathname.startsWith("/buysell/") &&
      !/^\/[^/]+\/buysell$/.test(location1.pathname)
    ) {
      // If localStorage.getItem("holding") is true, set it to false
      const isHolding = localStorage.getItem("holding");
      if (isHolding) {
        console.log('in hereee');
        localStorage.setItem("holding", "false");
        localStorage.removeItem("trade");
      }
    }
  }, [location1.pathname]); // Monitor route changes

  useEffect(() => {
    if (!status) {
      setIsVerified(true);
      setIsVerifiedOpen(true);
    }
  }, [status]);

  useEffect(() => {
    // const urlParams = new URLSearchParams(window.location.search);
    // const tokenFromURL = urlParams.get("token");

    // if (tokenFromURL) {
    //   // Store the token from the URL if it exists
    //   localStorage.setItem("gameToken", JSON.stringify(tokenFromURL));
    //   alert(`Token from URL: ${tokenFromURL}`);
    // }

    const checkAuthentication = async () => {
      try {
        const token = JSON.parse(localStorage.getItem("accessToken"));

        if (!!token && typeof token === "string" && token.trim().length > 0) {
          console.log("Token is present. Setting isAuthenticated to true.");
          setIsAuthenticated(true);
          // setIsVerified(true);
          // location("/feed");

          const currentPath = window.location.pathname;
          if (!unauthenticatedRoutes.includes(currentPath)) {
            if (currentPath === "/") {
              location("/profile");
            }
          }
        } else {
          setIsAuthenticated(false);
          // location("/");
          // location("/auth/login");
          // Handle token errors or expiration here
        }
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthentication();

    const handlePageReload = () => {
      window.addEventListener("beforeunload", checkAuthentication);
    };

    $("input, textarea, select").bind("blur", function (e) {
      window.scrollTo(0, 1);
    });

    handlePageReload();

    return () => {
      window.removeEventListener("beforeunload", checkAuthentication);
    };
  }, []);

  // ProtectedRoute and IsUserVerified now live at module scope — see the comment
  // above App(). Declaring them here is what made React remount every screen.

  window.sendDataToReactApp = function (data) {
    console.log("Data From Flutter::::::::::::", data);
    const jsonData = data;
    const deviceName = jsonData["Device-Name"];
    const deviceType = jsonData["Device-Type"];
    const fcmToken = jsonData["FCM-Token"];
    const deviceToken = jsonData["Device-Token"];
    localStorage.setItem("FCM", fcmToken);
    localStorage.setItem("DeviceName", deviceName);
    localStorage.setItem("DeviceType", deviceType);
    localStorage.setItem("DeviceToken", deviceToken);
  };

  // Tracker is hoisted to module scope too — as an inline declaration it was a
  // new type every render, so it remounted and re-fired trackPageView() on every
  // App render rather than once per navigation.

  return (
    <RouteGateContext.Provider value={routeGate}>
      {/* The app-wide loading overlay is gone. Native social sign-in used to dim
          and block the whole app behind a centred spinner; it now completes
          silently and the destination screen renders its own skeleton. */}


      <Tracker />

      <AppLinksMetaTags />

      {/* Mounted at last. The class has existed in ErrorBoundry.js all along but
          nothing ever rendered it, so any error thrown during render took the
          entire app down to a blank page with no way back. */}
      <ErrorBoundary>
      <Routes>
                <Route
            path="/contest/pdf"
            element={
              !status ? (
                <IsUserVerified />
              ) : (
                <ProtectedRoute element={<CompetitionRules />} />
              )
            }
          />

        <Route path="/" element={<AuthLayout />}>
          {/* Public: Cookie Policy */}
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route
            path="/dashboard"
            element={<ProtectedRoute element={<Dashboard />} />}
          />
          <Route
            path="/profile"
            element={
              !status ? (
                <IsUserVerified />
              ) : (
                <ProtectedRoute element={<ProfilePage />} />
              )
            }
          />


          <Route
            path="/feed"
            element={
              !status ? (
                <IsUserVerified />
              ) : (
                <ProtectedRoute element={<FeedPage />} />
              )
            }
          />
          <Route
            path="/leaderboard"
            element={
              !status ? (
                <IsUserVerified />
              ) : (
                <ProtectedRoute element={<LeaderBoardPage />} />
              )
            }
          />
          <Route
            path="/mobile-watchlist/:id"
            element={<MobileWatchlistPage />}
          />

          <Route
            path="/mobile-children-watchlist/:id"
            element={<ChildWatchlistPage />}
          />

          <Route
            path="/search"
            element={
              !status ? (
                <IsUserVerified />
              ) : (
                <ProtectedRoute element={<SearchPage />} />
              )
            }
          />

          {/* Legacy watchlist layout, kept for side-by-side comparison with /search */}
          <Route
            path="/oldsearch"
            element={
              !status ? (
                <IsUserVerified />
              ) : (
                <ProtectedRoute element={<SearchPage />} />
              )
            }
          />

          <Route
            path="/beforesearch"
            element={
              !status ? (
                <IsUserVerified />
              ) : (
                <ProtectedRoute element={<BeforeSearch />} />
              )
            }
          >

          </Route>
          <Route
            path="/search/:term"
            element={
              !status ? (
                <IsUserVerified />
              ) : (
                <ProtectedRoute element={<SearchPage />} />
              )
            }
          />
          <Route
            path="/create-post"
            element={
              !status ? (
                <IsUserVerified />
              ) : (
                <ProtectedRoute element={<CreatePostPage />} />
              )
            }
          />
          <Route
            path="/market-sentiment"
            element={
              !status ? (
                <IsUserVerified />
              ) : (
                <ProtectedRoute element={<MarketSentimentPage />} />
              )
            }
          />
          <Route
            path="/chat"
            element={
              !status ? (
                <IsUserVerified />
              ) : (
                <ProtectedRoute element={<ChatPage />} />
              )
            }
          />
          <Route
            path="/chatHistory"
            element={
              !status ? (
                <IsUserVerified />
              ) : (
                <ProtectedRoute element={<ChatHistoryPage />} />
              )
            }
          />
          <Route
            path="/chatHistory/:chatid/:chatgid"
            element={
              !status ? (
                <IsUserVerified />
              ) : (
                <ProtectedRoute element={<ChatHistoryPage />} />
              )
            }
          />
          <Route
            path="/chatHistory/:chatid"
            element={
              !status ? (
                <IsUserVerified />
              ) : (
                <ProtectedRoute element={<ChatHistoryPage />} />
              )
            }
          />
          <Route
            path="/buysell"
            element={
              !status ? (
                <IsUserVerified />
              ) : (
                <ProtectedRoute element={<LegacyBuySellRedirect to="/charts" portfolio="/portfolio" />} />
              )
            }
          />


          <Route

            path="/buysell/:stockname"

            element={

              !status ? (

                <IsUserVerified />

              ) : (

                <ProtectedRoute element={<LegacyBuySellRedirect to="/charts" portfolio="/portfolio" />} />

              )

            }

          />

          <Route
            path="/buysell/:id"
            element={
              !status ? (
                <IsUserVerified />
              ) : (
                <ProtectedRoute element={<LegacyBuySellRedirect to="/charts" portfolio="/portfolio" />} />
              )
            }
          />
          <Route
            path="/paid-content/:id"
            element={
              !status ? (
                <IsUserVerified />
              ) : (
                <ProtectedRoute element={<PaidContent />} />
              )
            }
          />
          <Route
            path="/charts"
            element={
              !status ? (
                <IsUserVerified />
              ) : (
                <ProtectedRoute element={<Char />} />
              )
            }
          />
          <Route
            path="/live-chart"
            element={
              !status ? (
                <IsUserVerified />
              ) : (
                <ProtectedRoute element={<LiveChart />} />
              )
            }
          />
          <Route
            path="/settings"
            element={
              !status ? (
                <IsUserVerified />
              ) : (
                <ProtectedRoute element={<Settings />} />
              )
            }
          />

          <Route
            path="/trading-floor"
            element={
              !status ? (
                <IsUserVerified />
              ) : (
                <ProtectedRoute element={<TradingFloor />} />
              )
            }
          />

          <Route
            path="/trading-floor/:id/:category_id"
            element={
              !status ? (
                <IsUserVerified />
              ) : (
                <ProtectedRoute element={<TradingDetail />} />
              )
            }
          />

          <Route
            path="/blank"
            element={
              !status ? (
                <IsUserVerified />
              ) : (
                <ProtectedRoute element={<Blank />} />
              )
            }
          />
          {/* The Edge — Candle Anatomy, Phase 1 prototype (URL only, no menu entry). */}
          <Route
            path="/edge/candle-anatomy"
            element={
              !status ? (
                <IsUserVerified />
              ) : (
                <ProtectedRoute element={<CandleAnatomyPage />} />
              )
            }
          />
          {/* The Edge — Momentum, Phase 2 pilot (URL only, no menu entry). */}
          <Route
            path="/edge/momentum"
            element={
              !status ? (
                <IsUserVerified />
              ) : (
                <ProtectedRoute element={<MomentumPage />} />
              )
            }
          />
          {/* The Edge — Structure, Read 03 (URL only, no menu entry). */}
          <Route
            path="/edge/structure"
            element={
              !status ? (
                <IsUserVerified />
              ) : (
                <ProtectedRoute element={<StructurePage />} />
              )
            }
          />
          <Route
            path="/home"
            element={
              !status ? (
                <IsUserVerified />
              ) : (
                <ProtectedRoute element={<HomePage />} />
              )
            }
          />
          <Route
            path="/edit-profile"
            element={
              !status ? (
                <IsUserVerified />
              ) : (
                <ProtectedRoute element={<EditProfile />} />
              )
            }
          />
          <Route
            path="/change-password"
            element={
              !status ? (
                <IsUserVerified />
              ) : (
                <ProtectedRoute element={<ChangePassword />} />
              )
            }
          />
          <Route
            path="/get-help"
            element={
              !status ? (
                <IsUserVerified />
              ) : (
                <ProtectedRoute element={<GetHelp />} />
              )
            }
          />
          <Route
            path="/about-qushi"
            element={
              !status ? (
                <IsUserVerified />
              ) : (
                <ProtectedRoute element={<AboutQushi />} />
              )
            }
          />
          {/* <Route
            path="/payment"
            element={
              !status ? (
                <IsUserVerified />
              ) : (
                <ProtectedRoute element={<StripePayment />} />
              )
            }
          /> */}
          <Route path="/auth/login" element={<Login />} />
          <Route path="/callback" element={<Testing />} />
          <Route path="/auth/verification" element={<Verification />} />
          {/* The "Create your free QUSHi account" screen on its own URL. Every
              "Create Account" link points here; /auth/register (the email form)
              is only reached from "Sign up with email" on this screen. */}
          <Route
            path="/auth/create-account"
            element={<MobileOnboarding initialStep={6} />}
          />
          <Route path="/auth/register" element={<Register />} />
          <Route path="/auth/register/:referralCode?" element={<Register />} />

          <Route path="/auth/forgot-password" element={<Password />} />
          <Route path="/reset-password/:token" element={<ResetPasswordToken />} />
            <Route
            path="/trading-detail/:id/:category_id"
            element={<TradingSingle />} />

          <Route
            path="/auth/trending-stocks"
            element={
              !status ? (
                <IsUserVerified />
              ) : (
                // <ProtectedRoute element={<TrendingStocksPage />} />
                <ProtectedRoute element={<SearchPage />} />
              )
            }
          />
          {/* <Route
            path="/subscriptions"
            element={
              !status ? (
                <IsUserVerified />
              ) : (
                <ProtectedRoute element={<Subscriptions />} />
              )
            }
          /> */}
          <Route
            path="/notifications"
            element={
              !status ? (
                <IsUserVerified />
              ) : (
                <ProtectedRoute element={<Notifications />} />
              )
            }
          />
          <Route
            path="/guide"
            element={
              !status ? (
                <IsUserVerified />
              ) : (
                <ProtectedRoute element={<Guide />} />
              )
            }
          />
          <Route
            path="/edit-post/:postId"
            element={
              !status ? (
                <IsUserVerified />
              ) : (
                <ProtectedRoute element={<CreatePostPage />} />
              )
            }
          />
          <Route
            path="/profile/:userId"
            element={
              !status ? (
                <IsUserVerified />
              ) : (
                <ProtectedRoute element={<ProfilePage />} />
              )
            }
          />
          {/* Live Portfolio: new layout + latest business logic */}
          <Route
            path="/portfolio/:userId"
            element={
              !status ? (
                <IsUserVerified />
              ) : (
                <ProtectedRoute element={<BuySellPage applyLogic={true} />} />
              )
            }
          />
          {/* Comparison route: new layout, presentation-only (no business-logic changes) */}
          <Route
            path="/newportfolio/:userId"
            element={
              !status ? (
                <IsUserVerified />
              ) : (
                <ProtectedRoute element={<BuySellPage />} />
              )
            }
          />
          {/* Comparison route: original (pre-redesign) Portfolio UI for another user */}
          <Route
            path="/oldportfolio/:userId"
            element={
              !status ? (
                <IsUserVerified />
              ) : (
                <ProtectedRoute element={<BuySellPage forceLegacy={true} />} />
              )
            }
          />
          <Route
            path="/followers/:userId"
            element={
              !status ? (
                <IsUserVerified />
              ) : (
                <ProtectedRoute element={<FollowersPage />} />
              )
            }
          />
          <Route
            path="/performance/:userId"
            element={
              !status ? (
                <IsUserVerified />
              ) : (
                <ProtectedRoute element={<PerformancePage />} />
              )
            }
          />

          <Route
            path="/feed/:postId"
            element={
              !status ? (
                <IsUserVerified />
              ) : (
                <ProtectedRoute element={<FeedPage />} />
              )
            }
          />
          <Route
            path="/chartSearch/:symbol"
            element={
              !status ? (
                <IsUserVerified />
              ) : (
                <ProtectedRoute element={<Char/>} />
              )
            }
          />
          <Route
            path="/saved-post"
            element={
              !status ? (
                <IsUserVerified />
              ) : (
                <ProtectedRoute element={<SavedPost />} />
              )
            }
          />
          {/* Live Portfolio: new layout + latest business logic */}
          <Route
            path="/portfolio"
            element={
              !status ? (
                <IsUserVerified />
              ) : (
                <ProtectedRoute element={<BuySellPage applyLogic={true} />} />
              )
            }
          />
          {/* Comparison route: new layout, presentation-only (no business-logic changes) */}
          <Route
            path="/newportfolio"
            element={
              !status ? (
                <IsUserVerified />
              ) : (
                <ProtectedRoute element={<BuySellPage />} />
              )
            }
          />
          {/* Comparison route: original (pre-redesign) Portfolio UI on mobile */}
          <Route
            path="/oldportfolio"
            element={
              !status ? (
                <IsUserVerified />
              ) : (
                <ProtectedRoute element={<BuySellPage forceLegacy={true} />} />
              )
            }
          />
            <Route
            path="/portfolionew"
            element={
              !status ? (
                <IsUserVerified />
              ) : (
                <ProtectedRoute element={<BuySellPage />} />
              )
            }
          />

          <Route
            path="/portfolionew/:userId"
            element={
              !status ? (
                <IsUserVerified />
              ) : (
                <ProtectedRoute element={<BuySellPage />} />
              )
            }
          />

          {/* <Route
            path="/cliff-notes"
            element={
              !status ? (
                <IsUserVerified />
              ) : (
                <ProtectedRoute element={<CliffNotes />} />
              )
            }
          /> */}

          <Route
            path="/"
            element={
              !JSON.parse(localStorage.getItem("accessToken")) ? (
                localStorage.getItem("qushi_onboarding_seen") !== "true" ? (
                  <MobileOnboarding />
                ) : (
                  <Navigate to="/auth/login" replace />
                )
              ) : (
                // Logged-in users go straight into the app at "/". This is the
                // entry point the native/mobile wrapper reopens to, so it must
                // NOT send them to the sign-in screen (that caused a "login
                // again on reopen" bug). The old StartScreen landing is no
                // longer shown. "Back to Welcome back" after sign-in is handled
                // by history alone — login lands on /profile, so one Back press
                // returns to /auth/login.
                <Navigate to="/profile" replace />
              )
            }
          />

          <Route path="/contests/:slug" element={<GameDetail />} />

          {/* The two shared league URLs, kept at the root because that's the
              shape organisers hand out: /join/ signals a private league, and
              /leagues/ a public one. Both normalise into the League Play area so
              the page renders inside the app chrome instead of bare. */}
          <Route path="/join/:slug" element={<JoinLinkRedirect to="/contest/join" />} />
          <Route path="/leagues/:slug" element={<JoinLinkRedirect to="/contest" />} />

          <Route path="/:slug/buysell/:ticker?" element={<LegacyBuySellRedirect to="/trade" />} />

          {/* Trading inside a league: the app's real Trade screen, scoped to the
              league by LeagueTradeContext. /:slug/buysell (the pre-revamp order
              form) now reroutes here, and its order-id edit links go to the
              league Portfolio (see LegacyRedirects). */}
          <Route path="/:slug/trade" element={<LeagueTradePage />} />

          <Route path="/:slug/charts" element={<GameCharts />} />

          {/* The older league Portfolio. Rerouted to the current one
              (see LegacyRedirects). */}
          <Route path="/:slug/portfolio" element={<LegacyLeaguePortfolioRedirect />} />

          {/* The league's own Watchlist tab. Client Rev 4: the tab must stay
              inside the league rather than hand the member back to the main
              QUSHi watchlist. */}
          <Route path="/:slug/watchlist" element={<LeagueWatchlist />} />

          <Route path="/:slug/leaderboard" element={<GameLeaderboard />} />

          {/* The STANDALONE leaderboard and the league profile reached from it.
              Client, League Play Dev Request, "Leaderboard Routing": "'View
              Results' CTA from any completed card must open the final
              leaderboard directly - not take people into the league home or the
              league shell… These leaderboard views must remain inside the main
              QUSHi site and must not open the league shell."

              Declared BEFORE the catch-all /:slug/* routes above would ever be
              consulted for these paths, and deliberately at an address
              leagueSlugFromPath does not resolve — that function is what turns
              the app's left rail into the league's nav, so an address it does
              not recognise keeps the main QUSHi shell by construction rather
              than by a flag every screen has to remember to pass.

              Both render the SAME components the in-league routes do
              (LeaderboardSecton and GameProfile); there is no duplicated
              leaderboard or profile. See utils/leaguePath.js. */}
          <Route path="/leaderboards/:slug" element={<LeagueLeaderboardStandalone />} />
          <Route
            path="/leaderboards/:slug/profile/:user_id"
            element={<GameProfile />}
          />
          {/* "Game details ›" from the standalone leaderboard's status line.
              Client: "'Game details' will take the user to the game details
              preview screen so they can see what the competition involves. There
              is no CTA on a league or game preview only details page. its a focus
              screen with only the back arrow."

              The SAME LeagueDetails screen the in-league three-dot menu opens,
              which already renders LeagueJoinPreview with showJoinCta={false}
              under a single back arrow. Only the membership gate differs — see
              the `standalone` flag there. */}
          <Route path="/leaderboards/:slug/details" element={<LeagueDetails />} />

          <Route path="/:slug/feeds" element={<GamePosts />} />

          {/* A single post opened INSIDE its competition.
              Client: "If someone interacts with a post that lives inside a
              league or game, tapping the notification should open that post
              inside that competition, not the main Feed."
              GamePosts renders FeedPage within the league shell, and FeedPage
              already reads :postId from the route to open that one post -- the
              same component and the same param the main /feed/:postId uses, so
              this is the league-scoped twin of that route rather than a second
              implementation of post detail. */}
          <Route path="/:slug/feeds/:postId" element={<GamePosts />} />

          <Route path="/:slug/create-feed" element={<GameCreatePost />} />

          <Route path="/:slug/edit-feed/:postId" element={<GameCreatePost />} />

          <Route path="/:slug/profile/:user_id" element={<GameProfile />} />

          <Route path="/:slug/chatHistory/:user_id" element={<GameMessage />} />

          <Route path="/:slug/portfolio/:user_id" element={<GameUserPortfolio />} />

          <Route path="/:slug/followers/:userId" element={<LeagueFollowerList />} />

          {/* EDIT ORDER*/}

          <Route
            path="/:slug/buysell/:id"
            element={
              !status ? (
                <IsUserVerified />
              ) : (
                <ProtectedRoute element={<LegacyBuySellRedirect to="/trade" />} />
              )
            }
          />



          <Route
            path="/contest"
            element={
              !status ? (
                <IsUserVerified />
              ) : (
                <GameContextProvider>
                  <ProtectedRoute element={<Contest />} />
                </GameContextProvider>
              )
            }
          >
            {/* Child routes nested under /contest */}
            <Route
              index
              element={
                !status ? (
                  <IsUserVerified />
                ) : (
                  <ProtectedRoute element={<LeaguePlayLanding />} />
                )
              }
            />
            {/* "View all" beside Public Leagues on League Play. The filter-bar
                grid this used to be (FindGames) is still mounted at
                /contest/find-games for anyone who needs the filters. */}
            <Route
              path="public-leagues"
              element={
                !status ? (
                  <IsUserVerified />
                ) : (
                  <ProtectedRoute element={<PublicLeagues />} />
                )
              }
            />
            <Route
              path="find-games"
              element={
                !status ? (
                  <IsUserVerified />
                ) : (
                  <ProtectedRoute element={<FindGames />} />
                )
              }
            />
            {/* "View all" beside My Leagues on League Play. Same screen shape as
                Public Leagues. The pre-revamp grid is still mounted below at
                /contest/find-my-games; nothing links to it. */}
            <Route
              path="my-games"
              element={
                !status ? (
                  <IsUserVerified />
                ) : (
                  <ProtectedRoute element={<MyLeagues />} />
                )
              }
            />
            <Route
              path="find-my-games"
              element={
                !status ? (
                  <IsUserVerified />
                ) : (
                  <ProtectedRoute element={<MyGames />} />
                )
              }
            />
              <Route
              path="cash-leagues"
              element={
                !status ? (
                  <IsUserVerified />
                ) : (
                  <ProtectedRoute element={<CashGames />} />
                )
              }
            />
            {/* The classic Create League form. Nothing current links here;
                the address now opens League Play, where Create League lives.
                Client: "Reachable with a current equivalent: reroute it to the
                current screen." */}
            <Route path="add-league" element={<Navigate to="/contest" replace />} />
            <Route
              path="create-public-league"
              element={
                !status ? (
                  <IsUserVerified />
                ) : (
                  <ProtectedRoute element={<CreatePublicLeagueWizard />} />
                )
              }
            />
            <Route
              path="create-private-league"
              element={
                !status ? (
                  <IsUserVerified />
                ) : (
                  <ProtectedRoute element={<CreatePrivateLeagueWizard />} />
                )
              }
            />
            <Route
              path="private-league-created"
              element={
                !status ? (
                  <IsUserVerified />
                ) : (
                  <ProtectedRoute element={<PrivateLeagueCreatedSuccess />} />
                )
              }
            />
            <Route
              path="public-league-created"
              element={
                !status ? (
                  <IsUserVerified />
                ) : (
                  <ProtectedRoute element={<PublicLeagueCreatedSuccess />} />
                )
              }
            />
            {/* A league's Portfolio tab, inside the league shell. /:slug/portfolio
                (the pre-revamp page) stays routed for the /oldcontest comparison. */}
            {/* Notifications and Chats, opened from INSIDE a league.
                Client Rev, desktop: "open the same existing Notifications or Chats
                content while preserving the current League shell and current
                league ID. When the user closes or goes back, return them to the
                league screen they came from."
                Same components the main app mounts at /notifications and /chat —
                not copies. Only the address changes, and the address is what the
                shell reads: /contest/<slug>/… already resolves to this league in
                leagueSlugFromPath, so the left rail stays the league's own menu
                with this league's ID, and Back walks straight to the league screen
                the bell was pressed on. */}
            <Route
              path=":slug/notifications"
              element={
                !status ? (
                  <IsUserVerified />
                ) : (
                  <ProtectedRoute element={<Notifications />} />
                )
              }
            />
            <Route
              path=":slug/chat"
              element={
                !status ? (
                  <IsUserVerified />
                ) : (
                  <ProtectedRoute element={<ChatPage />} />
                )
              }
            />
            {/* "League details" from the in-league three-dot menu. */}
            <Route
              path=":slug/details"
              element={
                !status ? (
                  <IsUserVerified />
                ) : (
                  <ProtectedRoute element={<LeagueDetails />} />
                )
              }
            />
            <Route
              path=":slug/portfolio"
              element={
                !status ? (
                  <IsUserVerified />
                ) : (
                  <ProtectedRoute element={<LeaguePortfolio />} />
                )
              }
            />
            {/* The organiser's rules screen — step 3 of Create League on its own.
                The pre-revamp form is still at :slug/edit; nothing links to it. */}
            <Route
              path=":slug/settings"
              element={
                !status ? (
                  <IsUserVerified />
                ) : (
                  <ProtectedRoute element={<LeagueSettings />} />
                )
              }
            />
            {/* Path 1 of the invite flow: what qushi.io/join/<slug> resolves to.
                The slug IS the invite code, so it's the whole input this page
                needs — no router state, refresh-safe, shareable. */}
            <Route
              path="join/:slug"
              element={
                !status ? (
                  <IsUserVerified />
                ) : (
                  <ProtectedRoute element={<PrivateLeaguePreview />} />
                )
              }
            />
            <Route
              path="private-league-preview"
              element={
                !status ? (
                  <IsUserVerified />
                ) : (
                  <ProtectedRoute element={<PrivateLeaguePreview />} />
                )
              }
            />
            <Route
              path="private-league-joined"
              element={
                !status ? (
                  <IsUserVerified />
                ) : (
                  <ProtectedRoute element={<PrivateLeagueJoinedSuccess />} />
                )
              }
            />

            {/* Members land inside the league; non-members still get the
                DetailGame join gate. See LeagueEntry. */}
            <Route path=":slug" element={<LeagueEntry />} />
            {/* The classic Edit League form and the older id-based edit page
                are rerouted to their current screens (see LegacyLeagueRedirects).
                edit-game/:id only knows a numeric id, so it opens My Leagues. */}
            <Route path=":slug/edit" element={<LegacyEditLeagueRedirect />} />
            <Route path="edit-game/:id" element={<Navigate to="/contest/my-games" replace />} />

            {/* <Route
    path="game-detail/:id"
    element={
      !status ? (
        <IsUserVerified />
      ) : (
        <ProtectedRoute element={<DetailGame />} />
      )
    }
  /> */}
          </Route>

          {/* Legacy League Play layout (the pre-revamp card grid as it is on
              main / UI/portfilio), kept for side-by-side comparison with the new
              /contest. Same Contest wrapper, so it also gets the old chrome —
              grey "Leagues" band, search bar and tab menu — which the wrapper
              hides only on /contest itself. Delete this route and
              pages/Game/legacy/ once the comparison is done. */}
          <Route
            path="/oldcontest"
            element={
              !status ? (
                <IsUserVerified />
              ) : (
                <GameContextProvider>
                  <ProtectedRoute element={<Contest />} />
                </GameContextProvider>
              )
            }
          >
            <Route
              index
              element={
                !status ? (
                  <IsUserVerified />
                ) : (
                  <ProtectedRoute element={<OldFindGames />} />
                )
              }
            />
          </Route>
        </Route>

        {/* Nothing matched. Without this the router renders NOTHING — no header,
            no bottom navigation, no content — and inside the Flutter WebView
            there is no browser chrome to escape with, so the only way out is to
            kill the app. That is what the client's "Watchlist whites out, and
            you have to close the app" was: a nav link to a route that does not
            exist. Any future bad link now lands on the feed instead of a dead
            white screen. */}
        <Route path="*" element={<Navigate to="/feed" replace />} />
      </Routes>
      </ErrorBoundary>

      {/* Contest Routes */}

      <LegalPopupModal
        isOpen={showPopup}
        handleLegalPopup={handleLegalPopup}
        checked={checked}
        handleClose={handleClose}
        handleCheckboxChange1={handleCheckboxChange1}
      />
    </RouteGateContext.Provider>
  );
}

export default App;
